import { HealthStatus } from '../health/health.model';

/** Use-case slugs matching the DB slugs used as URL path segments. */
export type UseCaseApp = 'supply-chain' | 'shortage-management' | 'integrator' | 'lepapp';

/** Backend-native states: DEPLOYED / NOT_DEPLOYED.
 *  DEPLOYING and ERROR are frontend-derived states based on ArgoCD healthStatus. */
export type DeploymentState = 'DEPLOYED' | 'NOT_DEPLOYED' | 'DEPLOYING' | 'ERROR';

/** Mirrors backend SyncStatus enum (JSON values as emitted by ArgoCD). */
export type ArgoSyncStatus = 'Synced' | 'OutOfSync' | 'Unknown';

/** Mirrors backend HealthStatus enum (JSON values as emitted by ArgoCD). */
export type ArgoHealthStatus =
  | 'Healthy'
  | 'Progressing'
  | 'Suspended'
  | 'Degraded'
  | 'Missing'
  | 'Unknown';

export interface ManagedApp {
  /** UseCaseApp enum value (e.g. 'supply-chain') */
  id: UseCaseApp;
  /** Transloco translation key for the app name */
  nameKey: string;
  /** Transloco translation key for the app description */
  descriptionKey: string;
  icon: string;
  deploymentState: DeploymentState;
  /** Direct URL to open the use-case application. Null if not configured. */
  appUrl: string | null;
  /** True if the app URL is currently reachable (real-time probe from BE). */
  online: boolean;
}

/** Matches TenantStatusDto from the BE */
export interface TenantStatus {
  organizationId: string;
  tenantName: string;
  state: 'PROVISIONING' | 'ACTIVE' | 'SUSPENDED' | 'REMOVING';
  managementUrl: string | null;
  /** ArgoCD sync status of the underlying connector application. */
  syncStatus: ArgoSyncStatus;
  /** ArgoCD health status of the underlying connector application. */
  healthStatus: ArgoHealthStatus;
  /** ISO-8601 timestamp of the last completed ArgoCD sync operation, or null. */
  lastSyncFinishedAt: string | null;
  createdDate: string;
  modifiedDate: string;
}

export interface ConnectorInfo {
  id: string;
  name: string;
  status: HealthStatus;
  managementUrl?: string;
  syncStatus: ArgoSyncStatus;
  healthStatus: ArgoHealthStatus;
}

/** Static display metadata for each UseCaseApp — values are Transloco translation keys */
export const USE_CASE_APP_METADATA: Record<
  UseCaseApp,
  { nameKey: string; descriptionKey: string; icon: string; useAppEndpoint?: boolean }
> = {
  'supply-chain': {
    nameKey: 'appManagement.apps.supplyChain.name',
    descriptionKey: 'appManagement.apps.supplyChain.description',
    icon: 'account_tree'
  },
  'shortage-management': {
    nameKey: 'appManagement.apps.shortageManagement.name',
    descriptionKey: 'appManagement.apps.shortageManagement.description',
    icon: 'show_chart'
  },
  integrator: {
    nameKey: 'appManagement.apps.integrator.name',
    descriptionKey: 'appManagement.apps.integrator.description',
    icon: 'hub',
    useAppEndpoint: true
  },
  lepapp: {
    nameKey: 'appManagement.apps.lepManagement.name',
    descriptionKey: 'appManagement.apps.lepManagement.description',
    icon: 'dashboard',
    useAppEndpoint: true
  }
};
